import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Botao } from '../../componentes/base';
import { Formulario, type DefCampo } from '../../componentes/formulario';
import { Drawer } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { api, ErroRequisicao, get, getTodos, mensagemDeErro, patch, post } from '../../nucleo/api';
import { nomePessoa } from '../../nucleo/formato';
import { consultar, enderecoDaConsulta, estadoDaResposta, fiscalDaConsulta, regimeDaConsulta, type EstadoConsulta } from './consulta';
import { camposFiscais, corpoFiscal, REGIMES } from './FiscalParceiro';
import { tiposDoPais, type TipoDocumentoPais } from '@malgoos/shared';
import { opcoesMoeda, opcoesPais, opcoesTipoDocumento, opcoesUf } from './paises';
import { SITUACOES_COMERCIAIS, situacoesDoForm, type PapelNovo, type Parceiro, type SituacaoComercial, type Tag } from './tipos';
import { CampoVerticais, verticaisDaCriacao, type ValorVerticais } from '../../componentes/verticais';

const CAMPOS_ENDERECO = ['cep', 'logradouro', 'numero', 'complemento', 'bairro', 'cidade', 'uf'] as const;
const ehBr = (v: Record<string, unknown>) => !v.pais || v.pais === 'BR';
/** Lote 19/10: EUA e Espanha têm o tipo do documento (EIN, SSN, ITIN; CIF, NIF, NIE) com máscara e controle. */
const comTipoDoPais = (v: Record<string, unknown>) => v.pais === 'US' || v.pais === 'ES';
const tiposDoParceiro = (v: Record<string, unknown>) => tiposDoPais(String(v.pais ?? 'BR'), v.tipo === 'empresa' ? 'juridica' : 'fisica');
/** O tipo escolhido vale para o país e para pessoa ou empresa. */
const tipoValido = (v: Record<string, unknown>) => comTipoDoPais(v) && (tiposDoParceiro(v) as readonly string[]).includes(String(v.documento_tipo ?? ''));
const TIPOS_FORA: readonly Exclude<TipoDocumentoPais, 'cpf' | 'cnpj'>[] = ['ein', 'ssn', 'itin', 'cif', 'nif', 'nie'];
/** C5: prefixo dos dados fiscais no formulário da criação (grupo "Dados da empresa"). */
const PREFIXO_FISCAL = 'fiscal.';
/** C5: WhatsApp e RG são campos personalizados (chaves `whatsapp` e `rg`): o WhatsApp no Contato, o RG junto do CPF. */
export const POSICAO_EXTRAS = { whatsapp: { depoisDe: 'telefone' }, rg: { depoisDe: 'documento' } };
/** C5: algum dado fiscal foi digitado ou veio da consulta? (isenção e produtor rural marcados contam; os padrões não.) */
export function temDadosFiscais(v: Record<string, unknown>): boolean {
  return Object.entries(v).some(
    ([k, x]) => k.startsWith(PREFIXO_FISCAL) && (x === true || typeof x === 'number' || (Array.isArray(x) ? x.length > 0 : typeof x === 'string' && x.trim() !== '')),
  );
}

/** Cadastro existente com o mesmo documento (409 `parceiro.documento_duplicado`, com o id em `meta`). */
interface Duplicado {
  id: string;
}

const RE_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/**
 * Fase 2 (W19, K5): o e-mail digitado já é de outro cadastro? Aviso NÃO bloqueante (o e-mail repetido continua permitido):
 * consulta o servidor 400 ms depois da última tecla, só com um e-mail completo e diferente do que o cadastro já tinha.
 */
function useEmailEmUso(email: string, ignorarId: string | undefined, original: string | null | undefined) {
  const [atrasado, setAtrasado] = useState('');
  useEffect(() => {
    const id = window.setTimeout(() => setAtrasado(email.trim()), 400);
    return () => window.clearTimeout(id);
  }, [email]);
  const pronto = RE_EMAIL.test(atrasado) && atrasado.toLowerCase() !== (original ?? '').trim().toLowerCase();
  const q = useQuery({
    queryKey: ['/cadastros/parceiros', 'email-em-uso', atrasado, ignorarId ?? null],
    queryFn: () => get<{ parceiro: { id: string; nome: string } | null }>('/cadastros/parceiros/email-em-uso', { email: atrasado, ...(ignorarId ? { ignorar_id: ignorarId } : {}) }),
    enabled: pronto,
    staleTime: 30_000,
  });
  return pronto ? (q.data?.parceiro ?? null) : null;
}

/**
 * Drawer de criação e edição de pessoa ou empresa. Fase 2 (W5): país (fora do BR, registro do país de origem e moeda
 * padrão), CPF e CNPJ com máscara e dica de validade, papel parceiro comercial, endereço principal opcional na criação,
 * consulta de CNPJ e CEP pelo servidor que só preenche campos vazios, e documento duplicado que oferece abrir a ficha ou
 * marcar o cadastro existente também com o papel.
 */
export function FormParceiro({
  aberto,
  aoFechar,
  papelNovo,
  empresa,
  parceiro,
  aoSalvar,
  contatoDe,
}: {
  aberto: boolean;
  aoFechar: () => void;
  papelNovo?: PapelNovo;
  empresa?: boolean;
  parceiro?: Parceiro | null;
  aoSalvar?: (id: string) => void;
  /** Lote 19/10 (item 26): "Adicionar contato" na ficha da empresa; a pessoa nasce vinculada a ela. */
  contatoDe?: string;
}) {
  const { t } = useTranslation('cadastros');
  const { t: tn } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const navegar = useNavigate();
  const setores = useQuery({ queryKey: ['cadastros', 'setores'], queryFn: () => getTodos<{ id: string; nome: string }>('/cadastros/setores'), enabled: aberto });
  const tags = useQuery({ queryKey: ['cadastros', 'tags'], queryFn: () => getTodos<Tag>('/cadastros/tags'), enabled: aberto });
  const editando = !!parceiro;
  const [consulta, setConsulta] = useState<{ cnpj?: EstadoConsulta; cep?: EstadoConsulta }>({});
  const [duplicado, setDuplicado] = useState<Duplicado | null>(null);
  const [emailDigitado, setEmailDigitado] = useState(parceiro?.email ?? '');
  const emailEmUso = useEmailEmUso(emailDigitado, parceiro?.id, parceiro?.email);
  // Lote 19/10: cadastro antigo de fora do Brasil com documento e sem tipo: a lista do tipo tem a opção vazia (o registro
  // livre de antes) e nada é exigido. Nos novos, a lista começa no primeiro tipo do país (pessoa ou empresa).
  const legado = !!parceiro?.documento && !parceiro.documento_tipo && !!parceiro.pais && parceiro.pais !== 'BR';
  const [vivo, setVivo] = useState<Record<string, unknown>>({ pais: parceiro?.pais || 'BR', tipo: parceiro?.tipo, documento_tipo: parceiro?.documento_tipo ?? null });
  const tipoPadrao = useMemo(() => {
    if (!comTipoDoPais(vivo) || tipoValido(vivo) || (legado && vivo.pais === parceiro?.pais && !vivo.documento_tipo)) return undefined;
    return { documento_tipo: tiposDoParceiro(vivo)[0] ?? null };
  }, [vivo, legado, parceiro?.pais]);
  async function consultarCnpj(cnpj: string) {
    setConsulta((c) => ({ ...c, cnpj: 'buscando' }));
    const r = await consultar('cnpj', cnpj);
    setConsulta((c) => ({ ...c, cnpj: estadoDaResposta(r, 'cnpj') }));
    if (r.situacao !== 'encontrado' || !r.dados) return null;
    const d = r.dados;
    // C5: na criação, os dados fiscais e o regime da Receita vão para o grupo "Dados da empresa", à vista e editáveis.
    const regime = editando ? null : regimeDaConsulta(d);
    return {
      nome: d.razao_social ?? d.nome_fantasia,
      email: d.email,
      telefone: d.telefone,
      ...(editando ? {} : enderecoDaConsulta(d.endereco as Record<string, unknown>, 'endereco.')),
      ...(editando ? {} : Object.fromEntries(Object.entries(fiscalDaConsulta(d)).map(([k, v]) => [`${PREFIXO_FISCAL}${k}`, v]))),
      ...(regime ? { 'regime.regime': regime.regime, 'regime.inicio': regime.inicio } : {}),
    };
  }

  async function consultarCep(cep: string) {
    setConsulta((c) => ({ ...c, cep: 'cepBuscando' }));
    const r = await consultar('cep', cep);
    setConsulta((c) => ({ ...c, cep: estadoDaResposta(r, 'cep') }));
    if (r.situacao !== 'encontrado' || !r.dados) return null;
    const { 'endereco.cep': _cep, ...resto } = enderecoDaConsulta(r.dados, 'endereco.');
    void _cep;
    return resto;
  }

  // C5 (lote de 09/10): a ordem e as áreas do antigo no Drawer atual: Identificação (com o status inicial na criação),
  // Contato, Dados da empresa (PJ do Brasil, recolhível, só na criação), Papéis e classificação (com as tags), Endereço
  // principal (criação) e Observações. WhatsApp e RG são campos personalizados (posicaoExtras); PIX e conta, só na ficha.
  const ID = t('novoCadastro.identificacao');
  const CONTATO = t('novoCadastro.contato');
  const EMPRESA = t('novoCadastro.empresa');
  const PAPEIS = t('novoCadastro.papeis');
  const OBS = t('novoCadastro.observacoes');
  const END = t('campos.enderecoPrincipal');
  const pjBr = (v: Record<string, unknown>) => ehBr(v) && v.tipo === 'empresa';
  const campos: DefCampo[] = [
    // Identificação
    { nome: 'tipo', rotulo: t('campos.tipo'), tipo: 'selecao', obrigatorio: true, largura: 'meia', grupo: ID, opcoes: [{ valor: 'empresa', rotulo: t('campos.empresa') }, { valor: 'pessoa', rotulo: t('campos.pessoa') }] },
    { nome: 'pais', rotulo: t('campos.pais'), tipo: 'selecao', obrigatorio: true, largura: 'meia', grupo: ID, opcoes: opcoesPais() },
    {
      nome: 'documento',
      rotulo: t('campos.cnpj'),
      tipo: 'documento',
      documento: 'cnpj',
      largura: 'meia',
      grupo: ID,
      visivel: pjBr,
      preencher: consultarCnpj,
      ajuda: consulta.cnpj ? t(`consulta.${consulta.cnpj}`) + (consulta.cnpj === 'preenchido' && !editando ? `. ${t('novoCadastro.fiscalNoGrupo')}` : '') : undefined,
    },
    { nome: 'documento', rotulo: t('campos.cpf'), tipo: 'documento', documento: 'cpf', largura: 'meia', grupo: ID, visivel: (v) => ehBr(v) && v.tipo !== 'empresa' },
    // Lote 19/10: nos EUA e na Espanha, o tipo (lista do país e de pessoa ou empresa) e o documento com a máscara dele; o
    // cadastro antigo sem tipo continua com o registro livre (a opção vazia da lista). Nos outros países, livre como antes.
    ...(['US', 'ES'] as const).flatMap((pais) =>
      (['empresa', 'pessoa'] as const).map(
        (tipo): DefCampo => ({
          nome: 'documento_tipo',
          rotulo: tn('documento.tipo'),
          tipo: 'selecao',
          largura: 'meia',
          grupo: ID,
          obrigatorio: !legado,
          placeholder: tn('documento.registro'),
          opcoes: opcoesTipoDocumento(pais, tipo === 'empresa' ? 'juridica' : 'fisica'),
          visivel: (v) => v.pais === pais && (v.tipo === 'empresa') === (tipo === 'empresa'),
        }),
      ),
    ),
    ...TIPOS_FORA.map((doc): DefCampo => ({ nome: 'documento', rotulo: tn(`documento.tipos.${doc}`), tipo: 'documento', documento: doc, largura: 'meia', grupo: ID, visivel: (v) => tipoValido(v) && v.documento_tipo === doc })),
    { nome: 'documento', rotulo: t('campos.registroExterior'), tipo: 'texto', largura: 'meia', grupo: ID, visivel: (v) => !ehBr(v) && !tipoValido(v), sensivel: 'documento' },
    { nome: 'moeda_padrao', rotulo: t('campos.moedaPadrao'), tipo: 'selecao', largura: 'meia', grupo: ID, opcoes: opcoesMoeda(), visivel: (v) => !ehBr(v) },
    // Lote 19/10 (item 24): o "Nome" da empresa é o nome da empresa (a razão social fica nos dados da empresa), e o da pessoa
    // é o nome completo; o mesmo campo com o rótulo pelo tipo, como o CPF e o CNPJ do documento.
    { nome: 'nome', rotulo: t('campos.nomeEmpresa'), tipo: 'texto', obrigatorio: true, grupo: ID, sensivel: 'nome', visivel: (v) => v.tipo === 'empresa' },
    { nome: 'nome', rotulo: t('campos.nomeCompleto'), tipo: 'texto', obrigatorio: true, grupo: ID, sensivel: 'nome', visivel: (v) => v.tipo !== 'empresa' },
    // Fase 2 (W19, K6): código do parceiro (o do sistema antigo ou o da casa), opcional e único por workspace.
    { nome: 'codigo', rotulo: t('campos.codigo'), tipo: 'texto', largura: 'meia', grupo: ID, ajuda: t('ajudaCodigo') },
    ...(editando
      ? []
      : ([
          {
            nome: 'status',
            rotulo: t('novoCadastro.status'),
            tipo: 'selecao',
            obrigatorio: true,
            largura: 'meia',
            grupo: ID,
            ajuda: t('novoCadastro.statusAjuda'),
            opcoes: [
              { valor: 'ativo', rotulo: t('novoCadastro.ativo') },
              { valor: 'inativo', rotulo: t('novoCadastro.inativo') },
            ],
          },
        ] satisfies DefCampo[])),
    {
      nome: 'parent_id',
      rotulo: t('campos.empresaVinculada'),
      tipo: 'busca',
      largura: 'meia',
      grupo: ID,
      busca: { caminho: '/cadastros/parceiros', params: { papel: 'empresa' }, rotulo: (i) => String(i.nome) },
      visivel: (v) => v.tipo === 'pessoa',
    },
    { nome: 'cargo', rotulo: t('campos.cargo'), tipo: 'texto', largura: 'meia', grupo: ID, visivel: (v) => v.tipo === 'pessoa' },
    // Contato
    { nome: 'email', rotulo: t('campos.email'), tipo: 'email', largura: 'meia', grupo: CONTATO },
    { nome: 'telefone', rotulo: t('campos.telefone'), tipo: 'telefone', largura: 'meia', grupo: CONTATO },
    // Dados da empresa: os campos do cartão "Dados fiscais" da ficha, à vista na criação (a consulta do CNPJ os preenche).
    ...(editando
      ? []
      : [
          ...camposFiscais(t, PREFIXO_FISCAL, EMPRESA).map((c) => ({ ...c, visivel: (v: Record<string, unknown>) => pjBr(v) && (!c.visivel || c.visivel(v)) })),
          { nome: 'regime.regime', rotulo: t('campos.regime'), tipo: 'selecao', largura: 'meia', grupo: EMPRESA, visivel: pjBr, opcoes: REGIMES.map((x) => ({ valor: x, rotulo: t(`valores.regime.${x}`) })) },
          { nome: 'regime.inicio', rotulo: t('novoCadastro.regimeInicio'), tipo: 'data', obrigatorio: true, largura: 'meia', grupo: EMPRESA, visivel: (v) => pjBr(v) && !!v['regime.regime'] },
        ] satisfies DefCampo[]),
    // Papéis e classificação (as tags entram no fim do grupo)
    { nome: 'e_cliente', rotulo: t('campos.cliente'), tipo: 'booleano', largura: 'terco', grupo: PAPEIS },
    { nome: 'e_fornecedor', rotulo: t('campos.fornecedor'), tipo: 'booleano', largura: 'terco', grupo: PAPEIS },
    { nome: 'e_parceiro', rotulo: t('campos.parceiroComercial'), tipo: 'booleano', largura: 'terco', grupo: PAPEIS },
    // Trilha 4 (ciclo do cliente): situação comercial do lado cliente (cliente, ex-cliente, migrado). crm8 (2): o lead é do CRM
    // e vira cliente no ganho; em Cadastros a opção "Lead" só aparece para o cadastro que já é lead (fornecedor ou parceiro que
    // também é lead do comercial), para não trocar o valor gravado sem querer.
    {
      nome: 'situacao_comercial',
      rotulo: t('situacoes.rotulo'),
      tipo: 'selecao',
      obrigatorio: true,
      largura: 'meia',
      grupo: PAPEIS,
      visivel: (v) => !!v.e_cliente,
      opcoes: situacoesDoForm(parceiro?.situacao_comercial).map((x) => ({ valor: x, rotulo: t(`situacoes.${x}`) })),
    },
    { nome: 'setor_id', rotulo: t('campos.setor'), tipo: 'selecao', largura: 'meia', grupo: PAPEIS, opcoes: (setores.data?.itens ?? []).map((s) => ({ valor: s.id, rotulo: s.nome })) },
    { nome: 'ranking_cliente', rotulo: t('campos.rankingCliente'), tipo: 'numero', min: 0, max: 5, passo: 1, largura: 'meia', grupo: PAPEIS },
    // Endereço principal opcional, só na criação (depois, na ficha). No BR, CEP com consulta e UF entre as 27.
    ...(editando
      ? []
      : ([
          {
            nome: 'endereco.cep',
            rotulo: t('campos.cep'),
            tipo: 'cep',
            largura: 'meia',
            grupo: END,
            visivel: ehBr,
            preencher: consultarCep,
            ajuda: consulta.cep ? t(`consulta.${consulta.cep}`) : undefined,
          },
          { nome: 'endereco.cep', rotulo: t('campos.codigoPostal'), tipo: 'texto', largura: 'meia', grupo: END, visivel: (v) => !ehBr(v), sensivel: 'oculto' },
          { nome: 'endereco.logradouro', rotulo: t('campos.logradouro'), tipo: 'texto', largura: 'meia', grupo: END, sensivel: 'oculto' },
          { nome: 'endereco.numero', rotulo: t('campos.numero'), tipo: 'texto', largura: 'terco', grupo: END, sensivel: 'oculto' },
          { nome: 'endereco.complemento', rotulo: t('campos.complemento'), tipo: 'texto', largura: 'terco', grupo: END, sensivel: 'oculto' },
          { nome: 'endereco.bairro', rotulo: t('campos.bairro'), tipo: 'texto', largura: 'terco', grupo: END, sensivel: 'oculto' },
          { nome: 'endereco.cidade', rotulo: t('campos.cidade'), tipo: 'texto', largura: 'meia', grupo: END },
          { nome: 'endereco.uf', rotulo: t('campos.uf'), tipo: 'selecao', largura: 'meia', opcoes: opcoesUf(), grupo: END, visivel: ehBr },
          { nome: 'endereco.uf', rotulo: t('campos.estadoProvincia'), tipo: 'texto', largura: 'meia', grupo: END, visivel: (v) => !ehBr(v) },
        ] satisfies DefCampo[])),
    // Observações
    { nome: 'observacoes', rotulo: t('campos.observacoes'), tipo: 'area', grupo: OBS, nomes: [parceiro?.nome] },
  ];

  const inicial: Record<string, unknown> = parceiro
    ? { ...parceiro, pais: parceiro.pais || 'BR', tag_ids: parceiro.tags.map((x) => x.id) }
    : {
        tipo: empresa || papelNovo === 'fornecedor' ? 'empresa' : papelNovo === 'contato' ? 'pessoa' : 'empresa',
        pais: 'BR',
        e_cliente: !papelNovo || papelNovo === 'cliente',
        e_fornecedor: papelNovo === 'fornecedor',
        e_parceiro: papelNovo === 'parceiro',
        ...(contatoDe ? { parent_id: contatoDe } : {}),
        situacao_comercial: 'cliente',
        ranking_cliente: 0,
        status: 'ativo',
        [`${PREFIXO_FISCAL}isento_ie`]: false,
        [`${PREFIXO_FISCAL}produtor_rural`]: false,
        [`${PREFIXO_FISCAL}inscricoes_produtor`]: [],
      };

  const [tagIds, setTagIds] = useState<string[]>((inicial.tag_ids as string[] | undefined) ?? []);
  // Ficha 2026-10-22: as verticais já na criação (null = o escopo de quem cria, como antes).
  const [verticais, setVerticais] = useState<ValorVerticais>(null);

  async function salvar(v: Record<string, unknown>) {
    setDuplicado(null);
    const br = ehBr(v);
    const endereco = Object.fromEntries(CAMPOS_ENDERECO.map((k) => [k, (v[`endereco.${k}`] as string | undefined)?.trim() || null]));
    const corpo = {
      tipo: v.tipo,
      nome: v.nome,
      codigo: String(v.codigo ?? '').trim() || null,
      pais: v.pais || 'BR',
      moeda_padrao: br ? null : v.moeda_padrao || null,
      documento: v.documento || null,
      // Lote 19/10: o tipo só nos EUA e na Espanha (sem tipo válido, o registro livre); nos outros países fica o gravado.
      ...(comTipoDoPais(v) ? { documento_tipo: tipoValido(v) ? v.documento_tipo : null } : br ? { documento_tipo: null } : {}),
      email: v.email || null,
      telefone: v.telefone || null,
      parent_id: v.tipo === 'pessoa' ? v.parent_id || null : null,
      cargo: v.tipo === 'pessoa' ? v.cargo || null : null,
      setor_id: v.setor_id || null,
      tag_ids: tagIds,
      e_cliente: !!v.e_cliente,
      e_fornecedor: !!v.e_fornecedor,
      e_parceiro: !!v.e_parceiro,
      ...(v.e_cliente && SITUACOES_COMERCIAIS.includes(v.situacao_comercial as SituacaoComercial) ? { situacao_comercial: v.situacao_comercial } : {}),
      ranking_cliente: Number(v.ranking_cliente ?? 0),
      observacoes: v.observacoes || null,
      extras: v.extras ?? {},
      ...(editando ? {} : { endereco }),
    };
    // C5: dados fiscais e regime digitados (ou trazidos pela consulta) no grupo "Dados da empresa" da criação.
    const pj = !editando && v.tipo === 'empresa' && br;
    const fiscal = pj && temDadosFiscais(v) ? corpoFiscal(v, PREFIXO_FISCAL) : null;
    const regime = pj && v['regime.regime'] && v['regime.inicio'] ? { regime: v['regime.regime'], inicio: v['regime.inicio'] } : null;
    // Status inicial inativo: sem dado fiscal a gravar depois, nasce inativo na mesma chamada; com ele, arquiva no fim.
    const inativo = !editando && v.status === 'inativo';
    const arquivarDepois = inativo && !!(fiscal || regime);
    let id = parceiro?.id;
    try {
      if (editando) await patch(`/cadastros/parceiros/${parceiro!.id}`, corpo);
      else id = (await post<{ id: string }>('/cadastros/parceiros', { ...corpo, ...(inativo && !arquivarDepois ? { ativo: false } : {}), ...verticaisDaCriacao(verticais) })).id;
    } catch (e) {
      const meta = e instanceof ErroRequisicao && e.corpo.code === 'parceiro.documento_duplicado' ? e.metaDoErro() : undefined;
      if (meta && typeof meta.id === 'string') setDuplicado({ id: meta.id });
      throw e;
    }
    // Dados fiscais e regime depois de criar, sem travar o cadastro (a falha parcial avisa, como antes).
    if (id && (fiscal || regime || arquivarDepois)) {
      try {
        if (fiscal) await api('PUT', `/cadastros/parceiros/${id}/fiscal`, fiscal);
        if (regime) await post(`/cadastros/parceiros/${id}/regimes`, regime);
      } catch (e) {
        toast.erro(mensagemDeErro(e));
      }
      if (arquivarDepois) await post(`/cadastros/parceiros/${id}/arquivar`, {}).catch((e: unknown) => toast.erro(mensagemDeErro(e)));
    }
    await qc.invalidateQueries({ queryKey: ['cadastros'] });
    await qc.invalidateQueries({ queryKey: ['/cadastros/parceiros'] });
    toast.sucesso(tn('comum.salvo'));
    fechar();
    if (id) aoSalvar?.(id);
  }

  function fechar() {
    setConsulta({});
    setDuplicado(null);
    setVerticais(null);
    aoFechar();
  }

  /** "Marcar também como": acrescenta o papel ao cadastro que já existe e abre a ficha dele. */
  async function marcarExistente(papel: PapelNovo) {
    if (!duplicado) return;
    try {
      const campo = papel === 'cliente' ? 'e_cliente' : papel === 'fornecedor' ? 'e_fornecedor' : 'e_parceiro';
      await patch(`/cadastros/parceiros/${duplicado.id}`, { [campo]: true });
      await qc.invalidateQueries({ queryKey: ['/cadastros/parceiros'] });
      toast.sucesso(t('duplicado.marcado', { papel: t(`papeis.${papel}`) }));
      const destino = duplicado.id;
      fechar();
      navegar(`/cadastros/cadastros/parceiro/${destino}`);
    } catch (e) {
      toast.erro(mensagemDeErro(e));
    }
  }

  const papelMarcar: PapelNovo | null = papelNovo && papelNovo !== 'contato' ? papelNovo : null;
  const tituloNovo = papelNovo === 'fornecedor' ? 'acoes.novoFornecedor' : papelNovo === 'contato' ? 'acoes.novoContato' : papelNovo === 'parceiro' ? 'acoes.novoParceiro' : empresa ? 'acoes.novaEmpresa' : 'acoes.novoCliente';

  const tagsDoCadastro = (
    <fieldset className="sm:col-span-6">
      <legend className="mb-1.5 text-sm font-medium text-tinta-2">{t('campos.tags')}</legend>
      <div className="flex flex-wrap gap-2">
        {(tags.data?.itens ?? []).map((tag) => {
          const marcada = tagIds.includes(tag.id);
          return (
            <button
              key={tag.id}
              type="button"
              aria-pressed={marcada}
              onClick={() => setTagIds(marcada ? tagIds.filter((x) => x !== tag.id) : [...tagIds, tag.id])}
              className={`rounded-full border px-3 py-1 text-xs font-medium ${marcada ? 'border-2 border-acento-forte bg-acento-suave text-acento-forte' : 'border-borda text-tinta-2 hover:text-tinta-1'}`}
            >
              {tag.nome}
            </button>
          );
        })}
      </div>
    </fieldset>
  );

  return (
    <Drawer
      aberto={aberto}
      aoFechar={fechar}
      titulo={editando ? nomePessoa(parceiro!.nome) : t(tituloNovo)}
      rodape={
        <>
          <Botao variante="fantasma" onClick={fechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" type="submit" form="form-parceiro">
            {tn('comum.salvar')}
          </Botao>
        </>
      }
    >
      {duplicado && (
        <div role="alert" data-duplicado="" className="mb-4 rounded-lg border border-laranja px-3 py-3 text-sm">
          <p className="font-medium text-tinta-1">{t('duplicado.titulo')}</p>
          <p className="mt-1 text-tinta-2">{t('duplicado.texto')}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Botao
              tamanho="sm"
              onClick={() => {
                const destino = duplicado.id;
                fechar();
                navegar(`/cadastros/cadastros/parceiro/${destino}`);
              }}
            >
              {t('acoes.abrirFicha')}
            </Botao>
            {papelMarcar && (
              <Botao tamanho="sm" variante="primario" onClick={() => void marcarExistente(papelMarcar)}>
                {t('acoes.marcarTambem', { papel: t(`papeis.${papelMarcar}`) })}
              </Botao>
            )}
          </div>
        </div>
      )}
      {emailEmUso && (
        <div role="status" data-email-em-uso="" className="mb-4 rounded-lg border border-laranja px-3 py-3 text-sm">
          <p className="font-medium text-tinta-1">{t('emailEmUso.titulo')}</p>
          <p className="mt-1 text-tinta-2">{t('emailEmUso.texto', { nome: nomePessoa(emailEmUso.nome) })}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Botao
              tamanho="sm"
              onClick={() => {
                const destino = emailEmUso.id;
                fechar();
                navegar(`/cadastros/cadastros/parceiro/${destino}`);
              }}
            >
              {t('acoes.abrirFicha')}
            </Botao>
          </div>
        </div>
      )}
      <Formulario
        id="form-parceiro"
        campos={campos}
        inicial={inicial}
        telaCampos="cadastros.parceiro"
        entidade="cadastros.parceiro"
        aoEnviar={salvar}
        aoMudar={(v) => {
          setEmailDigitado(String(v.email ?? ''));
          setVivo({ pais: v.pais, tipo: v.tipo, documento_tipo: v.documento_tipo });
        }}
        sobrepor={tipoPadrao}
        rotulosAtuais={parceiro?.parent_nome ? { parent_id: parceiro.parent_nome } : undefined}
        gruposRecolhiveis={[EMPRESA]}
        posicaoExtras={POSICAO_EXTRAS}
        fimDoGrupo={{ [PAPEIS]: tagsDoCadastro }}
      >
        {!editando && <CampoVerticais valor={verticais} aoMudar={setVerticais} />}
      </Formulario>
    </Drawer>
  );
}
